import { useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { EQ_FREQUENCIES, EQ_PRESETS } from '@/lib/dsp';
import { usePreferences } from '@/hooks/usePreferences';
import { EqGraph } from '@/components/settings/EqGraph';
import {
  clearAppShellCaches,
  clearAudioCache,
  formatBytes,
  getAudioCacheStats,
  isInstalledPwa,
  type AudioCacheStats,
} from '@/lib/audioOffline';

function formatBand(hz: number) {
  return hz >= 1000 ? `${hz / 1000}k` : String(hz);
}

function SettingsSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label className="settings-toggle-row">
      <span>{label}</span>
      <span className={`settings-switch${checked ? ' is-on' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={label}
        />
        <span className="settings-switch-track" aria-hidden>
          <span className="settings-switch-thumb" />
        </span>
      </span>
    </label>
  );
}

export function SettingsPanel() {
  const prefs = usePreferences();
  const presetIds = Object.keys(EQ_PRESETS).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  const presetValue =
    prefs.eqPresetId in EQ_PRESETS || prefs.eqPresetId === 'Custom'
      ? prefs.eqPresetId
      : 'Flat';

  const [audioStats, setAudioStats] = useState<AudioCacheStats>({
    count: 0,
    bytes: 0,
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const installed = isInstalledPwa();

  const refreshStats = useCallback(async () => {
    setAudioStats(await getAudioCacheStats());
  }, []);

  useEffect(() => {
    void refreshStats();
  }, [refreshStats]);

  const clearOfflineAudio = useCallback(async () => {
    setBusy(true);
    setStatus('');
    try {
      await clearAudioCache();
      await refreshStats();
      setStatus('Offline audio cleared.');
    } catch {
      setStatus('Could not clear offline audio.');
    } finally {
      setBusy(false);
    }
  }, [refreshStats]);

  const clearAllSiteData = useCallback(async () => {
    if (
      !window.confirm(
        'Delete preferences, offline audio, and cached site files on this device?',
      )
    ) {
      return;
    }
    setBusy(true);
    setStatus('');
    try {
      await clearAudioCache();
      await clearAppShellCaches();
      try {
        localStorage.removeItem('graystore');
        localStorage.removeItem('graystore.v1.0');
        localStorage.removeItem('tzg-preferences-v1');
      } catch {
        /* ignore */
      }
      await refreshStats();
      setStatus('Site data deleted. Reloading…');
      window.setTimeout(() => window.location.reload(), 500);
    } catch {
      setStatus('Could not delete site data.');
      setBusy(false);
    }
  }, [refreshStats]);

  return (
    <div className="settings-panel">
      <div className="catalog-toolbar">
        <div className="section-label">Settings</div>
      </div>

      <section className="settings-section">
        <h2 className="settings-heading">Appearance</h2>
        <div className="settings-row">
          <span>Theme</span>
          <div
            className={`settings-segmented${prefs.theme === 'dark' ? ' is-end' : ' is-start'}`}
            role="group"
            aria-label="Theme"
          >
            <span className="settings-segmented-thumb" aria-hidden />
            <button
              type="button"
              className={prefs.theme === 'light' ? 'is-active' : ''}
              aria-pressed={prefs.theme === 'light'}
              onClick={() => prefs.setTheme('light')}
            >
              Light
            </button>
            <button
              type="button"
              className={prefs.theme === 'dark' ? 'is-active' : ''}
              aria-pressed={prefs.theme === 'dark'}
              onClick={() => prefs.setTheme('dark')}
            >
              Dark
            </button>
          </div>
        </div>
      </section>

      <section className="settings-section">
        <h2 className="settings-heading">Audio</h2>
        <SettingsSwitch
          label="Normalize"
          checked={prefs.loudnessEnabled}
          onChange={prefs.setLoudnessEnabled}
        />

        <SettingsSwitch
          label="Late Night"
          checked={prefs.lateNightEnabled}
          onChange={prefs.setLateNightEnabled}
        />

        <div className="eq-panel">
          <div className="eq-panel-body">
            <EqGraph gains={prefs.eqGains} onChangeGain={prefs.setEqGain} />
            <div className="eq-faders" role="group" aria-label="EQ bands">
              {EQ_FREQUENCIES.map((hz, i) => (
                <label key={hz} className="eq-fader">
                  <span className="eq-fader-rail" aria-hidden />
                  <input
                    type="range"
                    min={-12}
                    max={12}
                    step={0.5}
                    value={prefs.eqGains[i] ?? 0}
                    aria-label={`${formatBand(hz)} Hz`}
                    onChange={(e) => prefs.setEqGain(i, Number(e.target.value))}
                  />
                  <span className="eq-fader-label">{formatBand(hz)}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="eq-panel-footer">
            <div className="select-field settings-select">
              <select
                id="eq-preset"
                aria-label="EQ preset"
                value={presetValue}
                onChange={(e) => {
                  const id = e.target.value;
                  if (id === 'Custom') return;
                  prefs.applyEqPreset(id);
                }}
              >
                {presetIds.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
                {presetValue === 'Custom' ? (
                  <option value="Custom">Custom</option>
                ) : null}
              </select>
              <ChevronDown
                className="select-chevron"
                size={16}
                aria-hidden
                strokeWidth={2}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="settings-section">
        <h2 className="settings-heading">Storage</h2>
        <p className="settings-storage-copy">
          {installed
            ? 'Played tracks are kept in a private app cache so repeats don’t re-download.'
            : 'Install this site as an app to keep played tracks in a private offline cache.'}{' '}
          Offline audio stays inside the app (not your Downloads folder).
        </p>
        <div className="settings-row">
          <span>Offline audio</span>
          <span className="settings-storage-meta">
            {audioStats.count
              ? `${audioStats.count} · ${formatBytes(audioStats.bytes)}`
              : 'Empty'}
          </span>
        </div>
        <div className="settings-row settings-row-actions">
          <button
            type="button"
            className="settings-action"
            disabled={busy || audioStats.count === 0}
            onClick={() => void clearOfflineAudio()}
          >
            Clear offline audio
          </button>
        </div>
        <div className="settings-row settings-row-actions">
          <button
            type="button"
            className="settings-action settings-action-danger"
            disabled={busy}
            onClick={() => void clearAllSiteData()}
          >
            Delete site data
          </button>
        </div>
        {status ? <p className="settings-storage-status">{status}</p> : null}
      </section>
    </div>
  );
}
